import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Mail,
  Users,
  TrendingUp,
  Eye,
  MousePointer,
  Loader2,
  Send,
  ArrowRight,
  CheckCircle2,
  Clock,
  Edit3,
  UserPlus,
  BarChart3,
  Megaphone,
  Radio,
} from "lucide-react";
import { colors, fonts, CONTACT_TYPES } from "../styles/theme";
import { useTenant } from "../contexts/TenantContext";
import { StatCard, Badge } from "../components/ui";
import {
  fetchContacts,
  fetchNewsletters,
  fetchEngagementStats,
  fetchDashboardStats,
  fetchNewsletterAnalyticsSummary,
  fetchNewsletterAnalytics,
  fetchLatestNewsletterOpens,
  deriveContactType,
  displayName,
  initials,
  fmtDate,
} from "../services/api";

/** Relative time display (e.g. "2m ago", "1h ago", "3d ago"). */
function timeAgo(ts) {
  const diff = Date.now() - new Date(ts).getTime();
  if (diff < 60000) return "just now";
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return `${Math.floor(diff / 86400000)}d ago`;
}

/** Strip merge tags and series prefix from newsletter subject for display. */
function displayTitle(subject) {
  if (!subject) return "Untitled";
  const stripped = subject.replace(/\{\{[^}]+\}\},?\s*/g, "").trim();
  if (stripped !== subject && /^[^:]+:\s+/.test(stripped)) {
    return stripped.replace(/^[^:]+:\s+/, "").trim() || stripped;
  }
  return stripped || subject;
}

export default function SalesDashboard() {
  const navigate = useNavigate();
  const tenant = useTenant();
  const [contactCount, setContactCount] = useState(0);
  const [recentContacts, setRecentContacts] = useState([]);
  const [campaignData, setCampaignData] = useState(null);
  const [engagementData, setEngagementData] = useState(null);
  const [dashStats, setDashStats] = useState(null);
  const [analyticsSummary, setAnalyticsSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [opensModal, setOpensModal] = useState(null);
  const [liveOpens, setLiveOpens] = useState(null);

  useEffect(() => {
    Promise.all([
      fetchContacts({ limit: 20, sort: "createdAt" }).catch(() => ({ total: 0, contacts: [] })),
      fetchNewsletters().catch(() => null),
      fetchEngagementStats().catch(() => null),
      fetchDashboardStats().catch(() => null),
      fetchNewsletterAnalyticsSummary().catch(() => null),
    ])
      .then(([contactsRes, campaigns, engagement, dash, summary]) => {
        setContactCount(contactsRes.total || 0);
        const nonTeam = (contactsRes.contacts || []).filter(
          (c) => deriveContactType(c.tags, c.ghlTags) !== "team",
        );
        setRecentContacts(nonTeam.slice(0, 8));
        setCampaignData(campaigns);
        setEngagementData(engagement);
        setDashStats(dash);
        setAnalyticsSummary(summary);
      })
      .finally(() => setLoading(false));
  }, []);

  // Live newsletter opens — initial load + 30s polling
  useEffect(() => {
    let cancelled = false;
    const load = () =>
      fetchLatestNewsletterOpens()
        .then((data) => { if (!cancelled) setLiveOpens(data); })
        .catch(() => {});
    load();
    const interval = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  const cStats = campaignData?.stats || {};
  const eStats = engagementData || {};
  const campaigns = campaignData?.campaigns || [];

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}>
        <Loader2 size={24} color={colors.accent} className="spin" />      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontFamily: fonts.heading, fontSize: 28, fontWeight: 700, color: colors.text, marginBottom: 4 }}>
          Sales Dashboard
        </h1>
        <p style={{ fontFamily: fonts.body, fontSize: 14, color: colors.textSecondary }}>
          {tenant.companyName} — Marketing &amp; Outreach
        </p>
      </div>

      {/* KPI Cards */}
      <div style={{ display: "flex", gap: 16, marginBottom: 32, flexWrap: "wrap" }}>
        <StatCard
          icon={Users}
          label="Active Contacts"
          value={dashStats?.contactBreakdown
            ? Object.entries(dashStats.contactBreakdown)
                .filter(([type]) => type !== "inactive")
                .reduce((sum, [, count]) => sum + count, 0)
                .toLocaleString()
            : contactCount.toLocaleString()}
          change="live"
          changeType="up"
          onClick={() => navigate("/contacts")}
        />
        <StatCard
          icon={UserPlus}
          label="New This Week"
          value={`+${dashStats?.contactGrowth?.thisWeek || 0}`}
          change={
            dashStats?.contactGrowth?.change
              ? `${dashStats.contactGrowth.change > 0 ? "+" : ""}${dashStats.contactGrowth.change}%`
              : ""
          }
          changeType={dashStats?.contactGrowth?.change >= 0 ? "up" : "down"}
          onClick={() => navigate("/contacts?sort=createdAt")}
        />
        <StatCard
          icon={Megaphone}
          label="Campaigns Sent"
          value={cStats.completedCount || campaigns.filter((c) => c.status === "completed").length || 0}
          change={`${cStats.totalRecipients?.toLocaleString() || 0} recipients`}
          changeType="up"
          onClick={() => navigate("/newsletters")}
        />
        <StatCard
          icon={BarChart3}
          label="Avg Open Rate"
          value={
            analyticsSummary?.avgOpenRate != null
              ? `${analyticsSummary.avgOpenRate.toFixed(1)}%`
              : eStats.openRate != null
                ? `${eStats.openRate.toFixed(1)}%`
                : "—"
          }
          change={
            analyticsSummary?.avgClickRate != null
              ? `${analyticsSummary.avgClickRate.toFixed(1)}% click rate`
              : eStats.clickRate != null
                ? `${eStats.clickRate.toFixed(1)}% click rate`
                : ""
          }
          changeType="up"
        />
      </div>

      {/* Newsletter Performance + Contact Growth */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 32 }}>
        {/* Newsletter Performance */}
        <div style={{ background: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 8, padding: 24 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
            <h3 style={{ fontFamily: fonts.heading, fontSize: 16, color: colors.text, margin: 0 }}>
              Newsletter Performance
            </h3>
            <span
              onClick={() => navigate("/newsletters")}
              style={{ fontSize: 12, color: colors.accent, cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}
            >
              View all <ArrowRight size={12} />
            </span>
          </div>
          {(dashStats?.newsletterPerformance || []).length > 0 ? (
            dashStats.newsletterPerformance.map((nl, i) => (
              <div
                key={nl.id}
                onClick={async () => {
                  setOpensModal({ subject: displayTitle(nl.subject), contacts: [], loading: true });
                  try {
                    const data = await fetchNewsletterAnalytics(nl.id);
                    setOpensModal({ subject: displayTitle(nl.subject), contacts: data.topContacts || [], loading: false });
                  } catch {
                    setOpensModal((prev) => prev ? { ...prev, loading: false, error: true } : null);
                  }
                }}
                style={{
                  padding: "10px 0",
                  borderBottom: i < dashStats.newsletterPerformance.length - 1 ? `1px solid ${colors.border}` : "none",
                  cursor: "pointer",
                  borderRadius: 4,
                  transition: "background 0.15s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = colors.bgActive)}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                  <div style={{ fontSize: 13, color: colors.text, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, marginRight: 12 }}>
                    {displayTitle(nl.subject)}
                  </div>
                  <div style={{ fontSize: 11, color: colors.textSecondary, fontFamily: fonts.mono, flexShrink: 0 }}>
                    {nl.sentAt ? fmtDate(nl.sentAt) : "—"}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11 }}>
                    <Eye size={10} color="#3b82f6" />
                    <span style={{ fontFamily: fonts.mono, fontWeight: 700, color: "#3b82f6" }}>{nl.uniqueOpens}</span>
                    <span style={{ color: colors.textSecondary }}>opens ({nl.openRate}%)</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11 }}>
                    <MousePointer size={10} color={colors.accent} />
                    <span style={{ fontFamily: fonts.mono, fontWeight: 700, color: colors.accent }}>{nl.uniqueClicks}</span>
                    <span style={{ color: colors.textSecondary }}>clicks ({nl.clickRate}%)</span>
                  </div>
                  <span style={{ fontSize: 10, color: colors.textSecondary, marginLeft: "auto" }}>{nl.totalSent} sent</span>
                </div>
              </div>
            ))
          ) : campaigns.length > 0 ? (
            campaigns.slice(0, 5).map((c) => (
              <div key={c.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0", borderBottom: `1px solid ${colors.border}` }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: colors.text, marginBottom: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name}</div>
                  <div style={{ fontSize: 11, color: colors.textSecondary }}>
                    {c.recipients > 0 ? `${c.recipients.toLocaleString()} sent` : "No recipients"} · {c.sentAt || c.scheduledAt ? fmtDate(c.sentAt || c.scheduledAt) : "Not scheduled"}
                  </div>
                </div>
                <Badge
                  color={c.status === "completed" ? colors.success : c.status === "scheduled" ? colors.warning : colors.textSecondary}
                  bg={c.status === "completed" ? "rgba(16,185,129,0.12)" : c.status === "scheduled" ? "rgba(245,158,11,0.12)" : "rgba(90,100,120,0.12)"}
                >
                  {c.status === "completed" ? <CheckCircle2 size={10} /> : c.status === "scheduled" ? <Clock size={10} /> : <Edit3 size={10} />}
                  {c.status}
                </Badge>
              </div>
            ))
          ) : (
            <div style={{ fontSize: 12, color: colors.textSecondary, padding: 16, textAlign: "center" }}>No newsletters yet</div>
          )}
        </div>

        {/* New Contacts */}
        <div style={{ background: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 8, padding: 24 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
            <h3 style={{ fontFamily: fonts.heading, fontSize: 16, color: colors.text, margin: 0 }}>
              New Contacts
            </h3>
            <span
              onClick={() => navigate("/contacts")}
              style={{ fontSize: 12, color: colors.accent, cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}
            >
              View all <ArrowRight size={12} />
            </span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 8 }}>
            {recentContacts.map((contact) => {
              const type = deriveContactType(contact.tags, contact.ghlTags);
              const typeInfo = CONTACT_TYPES[type] || CONTACT_TYPES.subscriber;
              return (
                <div
                  key={contact.id}
                  onClick={() => navigate(`/contacts?contact=${contact.id}`)}
                  style={{
                    padding: 12,
                    background: colors.bg,
                    border: `1px solid ${colors.border}`,
                    borderRadius: 8,
                    display: "flex",
                    gap: 10,
                    alignItems: "center",
                    cursor: "pointer",
                    transition: "border-color 0.15s",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.borderColor = colors.accent)}
                  onMouseLeave={(e) => (e.currentTarget.style.borderColor = colors.border)}
                >
                  <div style={{ width: 34, height: 34, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", background: typeInfo.bg, color: typeInfo.color, fontFamily: fonts.heading, fontWeight: 700, fontSize: 13, flexShrink: 0 }}>
                    {initials(contact)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontFamily: fonts.body, color: colors.text, fontWeight: 600 }}>{displayName(contact)}</div>
                    <div style={{ fontSize: 11, color: colors.textSecondary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {contact.company || contact.email}
                    </div>
                  </div>
                  <Badge color={typeInfo.color} bg={typeInfo.bg}>{typeInfo.label}</Badge>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Live: Latest Newsletter Opens */}
      {liveOpens?.newsletter && (
        <div style={{ background: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 8, padding: 24, marginBottom: 32 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ position: "relative", width: 10, height: 10 }}>
                <div style={{ position: "absolute", inset: 0, borderRadius: "50%", background: "#10b981", animation: "livePulse 2s ease-in-out infinite" }} />
                <div style={{ position: "absolute", inset: 2, borderRadius: "50%", background: "#10b981" }} />
              </div>
              <h3 style={{ fontFamily: fonts.heading, fontSize: 16, color: colors.text, margin: 0 }}>
                Live: {displayTitle(liveOpens.newsletter.subject)}
              </h3>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 12, color: colors.textSecondary, fontFamily: fonts.mono }}>
                {liveOpens.totalUniqueOpens} of {liveOpens.newsletter.totalSent} opened
                ({liveOpens.newsletter.totalSent > 0 ? ((liveOpens.totalUniqueOpens / liveOpens.newsletter.totalSent) * 100).toFixed(1) : 0}%)
              </span>
              <Radio size={12} color="#10b981" style={{ opacity: 0.6 }} />
            </div>
          </div>
          <style>{`@keyframes livePulse { 0%, 100% { opacity: 0.4; transform: scale(1); } 50% { opacity: 1; transform: scale(1.4); } }`}</style>
          {liveOpens.opens.length > 0 ? (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 8 }}>
              {liveOpens.opens.map((opener, i) => {
                const name = [opener.firstName, opener.lastName].filter(Boolean).join(" ");
                const ts = opener.timestamp ? timeAgo(opener.timestamp) : "";
                return (
                  <div
                    key={opener.email}
                    style={{
                      display: "flex", alignItems: "center", gap: 10, padding: "8px 12px",
                      background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: 8,
                      transition: "border-color 0.15s", cursor: "pointer",
                    }}
                    onClick={() => navigate(`/contacts?search=${encodeURIComponent(opener.email)}`)}
                    onMouseEnter={(e) => (e.currentTarget.style.borderColor = colors.accent)}
                    onMouseLeave={(e) => (e.currentTarget.style.borderColor = colors.border)}
                  >
                    <div style={{
                      width: 30, height: 30, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center",
                      background: "rgba(59,130,246,0.1)", color: "#3b82f6",
                      fontFamily: fonts.heading, fontWeight: 700, fontSize: 11, flexShrink: 0,
                    }}>
                      {name ? name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2) : <Eye size={12} />}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, color: colors.text, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {name || opener.email}
                      </div>
                      {name && (
                        <div style={{ fontSize: 10, color: colors.textSecondary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {opener.company || opener.email}
                        </div>
                      )}
                    </div>
                    <div style={{ fontSize: 10, color: colors.textSecondary, fontFamily: fonts.mono, flexShrink: 0, textAlign: "right" }}>
                      {ts}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ fontSize: 12, color: colors.textSecondary, padding: 16, textAlign: "center" }}>No opens yet — stay tuned!</div>
          )}
        </div>
      )}

      {/* Recent Activity + Recent Contacts */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 32 }}>
        {/* Recent Activity Feed */}
        <div style={{ background: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 8, padding: 24 }}>
          <h3 style={{ fontFamily: fonts.heading, fontSize: 16, color: colors.text, marginBottom: 20 }}>
            Recent Email Activity
          </h3>
          {(dashStats?.recentActivity || []).length > 0 ? (
            dashStats.recentActivity.slice(0, 15).map((evt, i) => {
              const iconMap = {
                open: { icon: Eye, color: "#3b82f6" },
                click: { icon: MousePointer, color: colors.accent },
                delivered: { icon: Send, color: colors.success },
              };
              const info = iconMap[evt.type] || { icon: Mail, color: colors.textSecondary };
              const Icon = info.icon;
              return (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "5px 0",
                    borderBottom: i < Math.min(dashStats.recentActivity.length, 15) - 1 ? `1px solid ${colors.borderLight}` : "none",
                  }}
                >
                  <div style={{ width: 20, height: 20, borderRadius: 4, display: "flex", alignItems: "center", justifyContent: "center", background: info.color + "18", flexShrink: 0 }}>
                    <Icon size={10} color={info.color} />
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 600, color: colors.textSecondary, textTransform: "capitalize", minWidth: 55 }}>{evt.type}</span>
                  <span style={{ fontSize: 11, color: colors.text, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{evt.email}</span>
                  <span style={{ fontSize: 10, color: colors.textSecondary, fontFamily: fonts.mono, flexShrink: 0 }}>{evt.timestamp ? fmtDate(evt.timestamp) : ""}</span>
                </div>
              );
            })
          ) : (
            <div style={{ fontSize: 12, color: colors.textSecondary, padding: 16, textAlign: "center" }}>No activity yet</div>
          )}
        </div>

        {/* Contact Type Breakdown */}
        <div style={{ background: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 8, padding: 24 }}>
          <h3 style={{ fontFamily: fonts.heading, fontSize: 16, color: colors.text, marginBottom: 20 }}>
            Contact Breakdown
          </h3>
          {dashStats?.contactBreakdown ? (
            Object.entries(dashStats.contactBreakdown)
              .sort(([, a], [, b]) => b - a)
              .map(([type, count], i, arr) => {
                const typeInfo = CONTACT_TYPES[type] || CONTACT_TYPES.subscriber;
                const maxCount = Math.max(...Object.values(dashStats.contactBreakdown), 1);
                return (
                  <div key={type} style={{ marginBottom: i < arr.length - 1 ? 14 : 0 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <Badge color={typeInfo.color} bg={typeInfo.bg}>{typeInfo.label}</Badge>
                      </div>
                      <span style={{ fontSize: 13, fontFamily: fonts.mono, fontWeight: 700, color: colors.text }}>
                        {count.toLocaleString()}
                      </span>
                    </div>
                    <div style={{ height: 6, background: colors.bgActive, borderRadius: 3, overflow: "hidden" }}>
                      <div
                        style={{
                          height: "100%",
                          width: `${(count / maxCount) * 100}%`,
                          background: typeInfo.color,
                          borderRadius: 3,
                          transition: "width 0.6s ease",
                        }}
                      />
                    </div>
                  </div>
                );
              })
          ) : (
            <div style={{ fontSize: 12, color: colors.textSecondary, padding: 16, textAlign: "center" }}>No data available</div>
          )}
        </div>
      </div>

      {/* Engagement Summary Cards */}
      <div style={{ background: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 8, padding: 24, marginBottom: 32 }}>
        <h3 style={{ fontFamily: fonts.heading, fontSize: 16, color: colors.text, marginBottom: 20 }}>
          Engagement Summary
        </h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 12 }}>
          <div style={{ padding: 14, background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: 6, textAlign: "center" }}>
            <Send size={18} color={colors.accent} style={{ marginBottom: 8 }} />
            <div style={{ fontSize: 20, fontWeight: 700, fontFamily: fonts.mono, color: colors.text }}>
              {(eStats.totalDelivered || 0).toLocaleString()}
            </div>
            <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 4 }}>Emails Delivered</div>
          </div>
          <div style={{ padding: 14, background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: 6, textAlign: "center" }}>
            <Eye size={18} color="#3b82f6" style={{ marginBottom: 8 }} />
            <div style={{ fontSize: 20, fontWeight: 700, fontFamily: fonts.mono, color: "#3b82f6" }}>
              {(eStats.totalOpens || 0).toLocaleString()}
            </div>
            <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 4 }}>Total Opens</div>
          </div>
          <div style={{ padding: 14, background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: 6, textAlign: "center" }}>
            <MousePointer size={18} color={colors.accent} style={{ marginBottom: 8 }} />
            <div style={{ fontSize: 20, fontWeight: 700, fontFamily: fonts.mono, color: colors.accent }}>
              {(eStats.totalClicks || 0).toLocaleString()}
            </div>
            <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 4 }}>Total Clicks</div>
          </div>
          <div style={{ padding: 14, background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: 6, textAlign: "center" }}>
            <TrendingUp size={18} color={colors.success} style={{ marginBottom: 8 }} />
            <div style={{ fontSize: 20, fontWeight: 700, fontFamily: fonts.mono, color: colors.success }}>
              {eStats.openRate != null ? `${eStats.openRate.toFixed(1)}%` : "—"}
            </div>
            <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 4 }}>Open Rate</div>
          </div>
          <div style={{ padding: 14, background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: 6, textAlign: "center" }}>
            <MousePointer size={18} color="#f59e0b" style={{ marginBottom: 8 }} />
            <div style={{ fontSize: 20, fontWeight: 700, fontFamily: fonts.mono, color: "#f59e0b" }}>
              {eStats.clickRate != null ? `${eStats.clickRate.toFixed(1)}%` : "—"}
            </div>
            <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 4 }}>Click Rate</div>
          </div>
        </div>
      </div>

      {/* Opens Detail Modal */}
      {opensModal && (
        <div
          onClick={() => setOpensModal(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 12, padding: 24, width: 520, maxHeight: "70vh", display: "flex", flexDirection: "column" }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <h3 style={{ fontFamily: fonts.heading, fontSize: 16, color: colors.text, margin: 0 }}>{opensModal.subject}</h3>
              <button onClick={() => setOpensModal(null)} style={{ background: "none", border: "none", color: colors.textSecondary, cursor: "pointer", fontSize: 18, padding: "0 4px" }}>×</button>
            </div>
            <div style={{ fontSize: 12, color: colors.textSecondary, marginBottom: 12 }}>
              {opensModal.loading ? "Loading..." : opensModal.error ? "Failed to load data" : `${opensModal.contacts.filter((c) => c.opens > 0).length} unique opens`}
            </div>
            {opensModal.loading ? (
              <div style={{ display: "flex", justifyContent: "center", padding: 32 }}>
                <Loader2 size={20} color={colors.accent} className="spin" />
              </div>
            ) : opensModal.error ? (
              <div style={{ color: colors.danger, fontSize: 12, padding: 16, textAlign: "center" }}>Could not load analytics</div>
            ) : (
              <div style={{ overflowY: "auto", flex: 1 }}>
                {opensModal.contacts.filter((c) => c.opens > 0).length === 0 ? (
                  <div style={{ color: colors.textSecondary, fontSize: 12, textAlign: "center", padding: 24 }}>No opens recorded yet</div>
                ) : (
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead>
                      <tr style={{ borderBottom: `2px solid ${colors.border}` }}>
                        <th style={{ textAlign: "left", padding: "6px 8px", color: colors.textSecondary, fontWeight: 600 }}>Email</th>
                        <th style={{ textAlign: "center", padding: "6px 8px", color: colors.textSecondary, fontWeight: 600 }}>Opens</th>
                        <th style={{ textAlign: "center", padding: "6px 8px", color: colors.textSecondary, fontWeight: 600 }}>Clicks</th>
                        <th style={{ textAlign: "right", padding: "6px 8px", color: colors.textSecondary, fontWeight: 600 }}>First Opened</th>
                      </tr>
                    </thead>
                    <tbody>
                      {opensModal.contacts
                        .filter((c) => c.opens > 0)
                        .map((c) => (
                          <tr key={c.email} style={{ borderBottom: `1px solid ${colors.borderLight}` }}>
                            <td style={{ padding: "6px 8px", color: colors.text }}>{c.email}</td>
                            <td style={{ padding: "6px 8px", textAlign: "center", fontFamily: fonts.mono, fontWeight: 700, color: "#3b82f6" }}>{c.opens}</td>
                            <td style={{ padding: "6px 8px", textAlign: "center", fontFamily: fonts.mono, fontWeight: 700, color: c.clicks > 0 ? colors.accent : colors.textSecondary }}>{c.clicks}</td>
                            <td style={{ padding: "6px 8px", textAlign: "right", color: colors.textSecondary, fontFamily: fonts.mono }}>{c.firstOpen ? fmtDate(c.firstOpen) : "—"}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
